import { ClientType } from '@weapp/ebdcoms';
import { utils } from '@weapp/ui';
import { b64DecodeUnicode, b64EncodeUnicode } from '@weapp/utils';
import { isJsonString } from '.';
import {
  BuildLib,
  FixedMenu,
  FixedPageId,
  MultiFill,
  PageModuleType,
  SystemMenu,
} from '../constants';
import ebdcoms from './ebdcoms';

// 表单单页跳转特殊处理
const FPageType = {
  List: 'LIST',
  Card: 'CARD',
};

type CorsParams = {
  type: string;
  pageId: string;
  appId: string;
  getRightButton?: any;
  mobileView?: boolean;
  urlParams?: any;
  showGroup?: boolean;
  menuInfo?: MenuInfo;
  clientType?: ClientType;
  pathname?: string;
  isUnitFill?: boolean;
  match?: any;
  location?: Location;
  history?: any;
  noSetTitle?: boolean;
};

export type MenuInfo = {
  pageParam?: any; // 菜单参数 / PageView自定义参数
  menuId?: string; // 菜单id 左侧或右侧菜单
  sysMenuId?: string; // 系统菜单id
  [key: string]: any;
};

// 判断当前选中的是否是设置菜单
const isSettingMenu = (url: string) => url.match(/setting\/setting-\d*/);

export const getApp = (type: string, pageId: string, pathname: string, appid: string) => {
  const { PageType, SystemPageType } = ebdcoms.get();
  let app = '';

  switch (type.toUpperCase()) {
    case PageType.Page:
      app = '@weapp/ebdpage';
      break;
    case PageType.Layout:
    case PageType.Search:
    case FPageType.List:
    case FPageType.Card:
    case PageType.ViewPort:
      /**
       * 针对会议模块的特殊处理: type - search, pageId - meeting_mt_root_view
       * ? 针对search，我们的pageId应该是纯数字组成的ID，其他情况先不继续放到我们的路由里
       * TODO: 除search外，其他类型也需要处理，后续处理时一并优化这个位置
       */
      if (type.toUpperCase() === PageType.Search && pageId && !/^\d+/.test(pageId)) {
        app = '';
      } else {
        app = '@weapp/ebdfpage';
      }
      break;
    case PageType.Workflow: {
      switch (pageId?.toUpperCase()) {
        case MultiFill.FillingTask:
        case MultiFill.TaskMonitoring:
          app = '@weapp/edc';
          break;
        default:
          /**
           * - appId: 应该是数字开头的
           * - pageId 应该存在
           * - 其余情况认为不应该走入我们的路由中来
           */
          if (appid && /^\d+/.test(appid) && pageId) {
            app = '@weapp/ebdfpage';
          } else {
            app = '';
          }
          break;
      }
      break;
    }
    case MultiFill.MultireportMonitor:
    case MultiFill.MultireportTask:
    case PageType.Report:
      if (/^\d+$/.test(pageId)) {
        app = '@weapp/edc';
      }
      break;
    case BuildLib.Template:
    case BuildLib.Clause:
      app = '@weapp/contract';
      break;
    case PageType.SystemPage:
      // 数据列表
      if (pageId === SystemPageType.DataList) {
        app = '@weapp/formreport';
      } else if (pageId === SystemPageType.MeetingRoomInfo) {
        app = '@weapp/meeting';
      }
      break;
    case PageType.Link:
      // 督查督办地址
      if (pathname.includes('/govern_')) {
        app = '@weapp/govern';
      }
      break;
    case FixedMenu.Base:
      if (pageId?.toLocaleUpperCase() === FixedPageId.AccessLog) {
        app = '@weapp/ebdapp';
      } else {
        app = '@weapp/ebdfpage';
      }
      break;
    case FixedMenu.Setting:
      app = '@weapp/appbuilder';
      break;
    case PageType.Scene:
      app = '@weapp/scene';
      break;
    case SystemMenu.System:
    case SystemMenu.CusWorkFlow:
    case SystemMenu.FormDataReport:
    case SystemMenu.WorkFlowList:
    case SystemMenu.Project:
    case SystemMenu.Folder:
    case SystemMenu.Intelement:
    case SystemMenu.WorkFlowStatistic:
      app = '@weapp/cusapp';
      break;
    case PageType.Combinate.toUpperCase():
      app = '@weapp/appbuilder';
      break;
    default:
      break;
  }
  return app;
};

export const getCompName = (
  type: string = '',
  pageId: string,
  clientType: ClientType,
  pathname: string,
) => {
  const { PageType, SystemPageType } = ebdcoms.get();
  const isMobile = clientType !== 'PC';

  switch (type.toUpperCase()) {
    case PageType.Page:
      return isMobile ? 'MPageView' : 'PageView';
    case PageType.Search:
      return isMobile ? 'MTableViewList' : 'TableViewList';
    case PageType.ViewPort:
      return isMobile ? 'MViewPortPage' : 'ViewPortPage';
    case PageType.Workflow: {
      switch (pageId?.toUpperCase()) {
        case MultiFill.FillingTask:
          return 'MultipleTaskFill';
        case MultiFill.TaskMonitoring:
          return 'MultipleTaskMonitor';
        default:
          return isMobile ? 'MWorkFlowList' : 'WorkFlowList';
      }
    }
    case MultiFill.MultireportMonitor:
      return 'MultipleTaskMonitor';
    case MultiFill.MultireportTask:
      return 'MultipleTaskFill';
    case BuildLib.Template:
      return 'TemplateLibraryPage';
    case BuildLib.Clause:
      return 'ClauseLibraryPage';
    case PageType.Layout:
      return isMobile ? 'EbMCardDetailView' : 'EBCardViewPage';
    case PageType.Report:
      // 报表pc和移动端使用同一个组件
      return 'ReportAnalysisView';
    case PageType.SystemPage: {
      // 数据列表
      if (pageId === SystemPageType.DataList) {
        return 'FormReportDatalist';
      }

      if (pageId === SystemPageType.MeetingRoomInfo) {
        return 'MRoomUsageCom';
      }
      break;
    }
    case PageType.Link:
      // 督查督办地址
      if (pathname.includes('/govern_')) {
        return 'GovernAppCenter';
      }
      return '';
    case FixedMenu.Base:
      if (pageId?.toLocaleUpperCase() === FixedPageId.AccessLog) {
        return 'Log';
      }
      return isMobile ? 'EbMStaticCom' : 'EbStaticCom';
    case FixedMenu.Setting:
      if (isSettingMenu(pathname)) {
        return 'SettingComp';
      }
      return '';
    case PageType.Scene:
      return 'Render';
    case SystemMenu.System:
    case SystemMenu.CusWorkFlow:
    case SystemMenu.FormDataReport:
    case SystemMenu.WorkFlowList:
    case SystemMenu.Project:
    case SystemMenu.Folder:
    case SystemMenu.Intelement:
    case SystemMenu.WorkFlowStatistic:
      return 'ModuleContent';
    case PageType.Combinate.toUpperCase():
      return 'CombinedPagePreDialogComp';
    default:
      return '';
  }
};

const getModuleProps = (pageType: string, pageId: string, urlParams: any) => {
  if (utils.isEmpty(pageType)) return {};

  let corsProps = {};

  switch (pageType.toUpperCase()) {
    case SystemMenu.System: {
      if (!utils.isEmpty(pageId)) {
        const [, _moduleName = ''] = pageId.split('/');

        if (_moduleName) {
          corsProps = {
            moduleName: _moduleName,
            pageId,
          };
        }
      }
      break;
    }

    case SystemMenu.CusWorkFlow:
      corsProps = {
        moduleName: 'workflow',
        isWfCLCombList: true,
        pageId,
      };
      break;

    case SystemMenu.WorkFlowStatistic:
      corsProps = {
        moduleName: 'workflow',
        pageId,
      };
      break;

    case SystemMenu.WorkFlowList: {
      const { menuName } = urlParams || {};
      const tabs = localStorage.getItem('eb_workflowlist_tabs');

      let _menuName = '';
      try {
        // 处理编码后的字符串存在+号，通过history.push方法更新到路由上时，转变成%20的问题
        _menuName = decodeURIComponent(menuName);
        // %20经过decodeURIComponent解码后，会变成空格，此处重新替换成+号
        _menuName = _menuName.replace(/\s/g, '+');
        // 对菜单名称进行解码，菜单名称是通过b64EncodeUnicode进行编码的
        _menuName = b64DecodeUnicode(_menuName);
      } catch (error) {
        _menuName = menuName;
      }
      // menuName 编译之后不准确，手动获取
      if (_menuName?.includes(' ')) {
        const params: Record<string, any> = {};

        location.href
          .split('?')[1]
          .split('&')
          .forEach((param: string) => {
            const [name, value] = param.split('=');

            params[name] = value;
          });

        _menuName = params.menuName;
      }

      corsProps = {
        moduleName: 'workflow/list',
        currItem: {
          pageId,
          tabs: tabs || '{}',
          menuName: _menuName,
        },
      };
      break;
    }

    case SystemMenu.FormDataReport:
      corsProps = {
        moduleName: 'formreport',
        pageId,
      };
      break;

    case SystemMenu.Folder:
      corsProps = {
        moduleName: 'doc',
        pageId,
      };
      break;

    default:
      corsProps = {
        moduleName: pageType,
        pageId,
      };
      break;
  }

  return corsProps;
};

export const getCorsProps = (corsParams: CorsParams) => {
  const {
    type,
    pageId,
    appId,
    getRightButton,
    mobileView,
    urlParams,
    showGroup,
    menuInfo,
    clientType,
    pathname,
    isUnitFill,
    match,
    location,
    history,
    noSetTitle,
  } = corsParams || {};
  const { PageType, SystemPageType } = ebdcoms.get();
  const { id: urlParamsId, module } = urlParams || {};
  let corsProps = {};
  const isMobile = clientType !== 'PC';
  const getReloadKey = () => {
    // 菜单点击，history.key变更透传
    if (corsParams?.match?.isExact) {
      return corsParams?.history?.location?.key;
    }
  };
  const {
    pageParam, menuId, searchId, sysMenuId, defaultProps = {},
  } = menuInfo || {};
  switch (type?.toUpperCase()) {
    case PageType.Page:
      corsProps = {
        appId,
        pageId,
        type: module || PageModuleType.Eb,
        pageParam: { ...pageParam, searchId },
        // 是否需要设置页面标题
        noSetTitle: noSetTitle || false,
      };
      break;
    case PageType.ViewPort:
      corsProps = {
        appId,
        pageId,
        type: PageModuleType.EbForm,
        pageParam,
        comParams: { ...urlParams },
        searchId,
      };
      break;
    case PageType.Search:
      corsProps = {
        pageId,
        // 由于移动端采用多页面缓存机制，追加pageId字段，用户区分不同页面返回的DOM
        getTitleRightRender: getRightButton
          ? (dom: any, group: any) => getRightButton(pageId, dom, group)
          : null,
        appPreviewInfo: { showGroup },
        comParams: { ...urlParams },
        reloadKey: getReloadKey(),
        searchId,
        appId,
      };
      if (pageParam && pageParam.includes('FORMFIELDS')) {
        // 表格需要参数，才能解析表单字段
        corsProps = {
          ...corsProps,
          menuParams: {
            openSource: 'appmenu',
            targetConditionId: menuId,
          },
        };
      }
      break;
    case PageType.Workflow: {
      switch (pageId?.toUpperCase()) {
        case MultiFill.TaskMonitoring:
        case MultiFill.FillingTask:
          corsProps = {
            appId,
            pageId,
            appid: appId,
            isUnitFill,
          };
          break;
        default:
          corsProps = {
            appId,
            pageId,
            reloadKey: getReloadKey(),
            routePath:
              !match.url.includes(type) && !match.url.includes(`${appId}_${pageId}`)
                ? `${match.url}/${type}/${appId}_${pageId}`
                : match.url,
          };

          break;
      }
      break;
    }
    case MultiFill.MultireportTask:
    case MultiFill.MultireportMonitor:
      corsProps = {
        appId,
        pageId,
        appid: appId,
      };
      break;
    case PageType.Layout: {
      let params = pageId?.split('/');
      let selectLayout: any;

      if (pageId.includes('_')) {
        params = pageId?.split('_');
      }
      const dataId = urlParams.id || '0';

      // type:  0 - 显示布局, 1 - 新建布局, 2 - 编辑布局
      const _type = params.length ? params[0] : '';
      let _objid = params.length > 1 ? params[1] : '';

      if (!_objid) {
        _objid = pathname?.split('/').pop() || '';
        if (_objid.includes('-')) {
          _objid = _objid?.split('-').shift() || '';
        }
      }

      // 新建布局dataid为0
      const isNewLayout = _type === '1';
      const _dataId = isNewLayout ? '0' : dataId;

      // 补充Layout参数
      if (menuInfo?.selectLayout && isJsonString(menuInfo?.selectLayout)) {
        selectLayout = JSON.parse(menuInfo.selectLayout);
      }
      const menuParams: any = {
        layoutid: selectLayout?.id,
        menuName: b64EncodeUnicode(menuInfo?.menuName ? menuInfo.menuName : ''),
        selectedId: menuId,
        terminal: isMobile ? 'H5' : 'PC',
      };

      if (isMobile) {
        const extraProps = {
          reloadEvent() {
            const pageSDK = window.ebuilderSDK?.getPageSDK();

            pageSDK?.emit('onrefresh', true);
          },
        };

        corsProps = {
          appId,
          paramsFromParent: {
            objid: _objid,
            type: _type,
            dataid: _dataId,
            ...(isNewLayout ? extraProps : {}),
            // ...urlParams,
          },
          menuParams,
        };
      } else {
        corsProps = {
          appId,
          paramsFromParent: {
            objid: _objid,
            type: _type,
          },
          menuParams,
          reloadKey: getReloadKey(),
          idFromParent: _dataId,
          comParams: urlParams,
        };
      }
      if (pageParam && pageParam.includes('FORMFIELDS')) {
        // 表格需要参数，才能解析表单字段
        corsProps = {
          ...corsProps,
          ownParams: {
            openSource: 'appmenu',
            targetConditionId: menuId,
          },
        };
      }
      break;
    }
    case PageType.Report:
      corsProps = {
        appId,
        reportId: pageId,
        notShowTitle: !!getRightButton,
        // 由于移动端采用多页面缓存机制，追加pageId字段，用户区分不同页面返回的DOM
        getRightButton: getRightButton
          ? (dom: any, group: any) => getRightButton(pageId, dom, group)
          : null,
        mobileView,
        sysMenuId,
      };
      break;
    case PageType.SystemPage: {
      // 数据列表
      if (pageId === SystemPageType.DataList) {
        corsProps = {
          formId: urlParamsId,
          appId,
        };
      } else if (SystemPageType.MeetingRoomInfo) {
        corsProps = {
          match,
          location,
          history,
          store: window.weappMeeting?.mDetailStore || {},
          appId,
        };
      }
      break;
    }
    case PageType.Link:
      // 督查督办地址
      if (pathname?.includes('/govern_')) {
        const [, governPathStr] = pathname.split('/govern_');
        const governPath = atob(governPathStr.split('/')[0]);

        corsProps = {
          appId,
          governPath,
        };
      }
      break;
    case FixedMenu.Base:
      if (pageId?.toLocaleUpperCase() === FixedPageId.AccessLog) {
        corsProps = { appId, type: pageId };
      } else {
        corsProps = {
          params: { appId },
          type: pageId, // 由于移动端采用多页面缓存机制，追加pageId字段，用户区分不同页面返回的DOM
          getTitleRightRender: getRightButton
            ? (dom: any, group: any) => getRightButton(pageId, dom, group)
            : null,
          appPreviewInfo: { showGroup },
        };
      }
      break;
    case FixedMenu.Setting: {
      // 区分前后台
      const isBackend = /\/ebdapp\/build\//.test(pathname || window?.location?.pathname || '');
      const terminal = isBackend ? 'backend' : 'front';
      corsProps = { appId, terminal, settingId: sysMenuId };
      break;
    }
    case PageType.Scene:
      corsProps = { pageId, isDialog: true };
      break;
    case SystemMenu.System:
    case SystemMenu.CusWorkFlow:
    case SystemMenu.FormDataReport:
    case SystemMenu.WorkFlowList:
    case SystemMenu.Project:
    case SystemMenu.Folder:
    case SystemMenu.Intelement:
    case SystemMenu.WorkFlowStatistic:
      corsProps = getModuleProps(type, pageId, urlParams);
      break;
    case PageType.Combinate.toUpperCase():
      corsProps = {
        pageId,
        appId,
        terminalType: 'PC',
        hasDialog: false,
        comParams: urlParams,
        needUrl: true,
        isFront: 1,
      };
      break;
    case PageType.BussComp:
      corsProps = { ...urlParams, ...defaultProps };
      break;
    default:
      break;
  }

  corsProps = { ...corsProps, comParams: { ...urlParams } };

  return corsProps;
};
